
import { defineComponent, ref, reactive, onMounted, onBeforeUpdate, computed, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import { useHead } from '@vueuse/head'

import { RepositoryFactory } from '@/repositories/RepositoryFactory'
const SeasonGroupRepository = RepositoryFactory.get('seasonGroup')
const FieldGroupRepository = RepositoryFactory.get('fieldGroup')

import draggable from 'vuedraggable'

import BaseBreadcrumb, { Breadcrumb } from '@/components/BaseBreadcrumb.vue'
import BaseDialog from '@/components/BaseDialog.vue'

import { Dialog } from '@/types/dialog'
import { Season } from '@/types/season'
import { Group } from '@/types/group'
import { FieldGroup } from '@/types/fieldGroup'
import { EntryTeam } from '@/types/entryTeam'

interface CustomForm {
  id?: number
  priority?: number
  name: string
  match_type_id: number
  field_group_id?: number
  groups?: Group[]

  newGroups?: GroupForm[]
  temporary_id?: number
}

interface GroupForm {
  id?: number
  priority?: number
  name: string
  group_code: string
  teams?: EntryTeam[]

  temporary_id?: number
}

export default defineComponent({
  name: 'SeasonGroupCreate',
  inheritAttrs: false,
  components: {
    draggable,
    BaseBreadcrumb,
    BaseDialog
  },
  setup() {
    const route = useRoute()
    const store = useStore()

    onMounted(() => {
      getFieldGroups()
      getGroups()
    })

    const season: {
      data?: Season
    } = reactive({
      data: undefined
    })

    const groups: {
      list: CustomForm[]
      newCustoms: CustomForm[]

      selectedCustom?: CustomForm
      selectedGroup?: GroupForm

      isLoading: boolean
    } = reactive({
      list: [],
      newCustoms: [],

      selectedCustom: undefined,
      selectedGroup: undefined,

      isLoading: false
    })

    const getGroups = async () => {
      try {
        groups.isLoading = true

        const { season_id } = route.params

        const res = await SeasonGroupRepository.getGroups(season_id)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          season.data = data.season

          groups.list = data.groups
          groups.list.forEach((custom: CustomForm) => {
            custom.newGroups = []
          })
        }
      } catch (error) {
        //
      } finally {
        groups.isLoading = false
      }
    }

    const fieldGroups: {
      list: FieldGroup[]
      selected?: FieldGroup

      isLoading: boolean
    } = reactive({
      list: [],
      selected: undefined,

      isLoading: false
    })

    const getFieldGroups = async () => {
      try {
        fieldGroups.isLoading = true

        const res = await FieldGroupRepository.getFieldGroups()

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          fieldGroups.list = data.groups
        }
      } catch (error) {
        //
      } finally {
        fieldGroups.isLoading = false
      }
    }

    const customNameInputs = ref([])

    onBeforeUpdate(() => {
      customNameInputs.value = []
    })

    const addCustomBlock = () => {
      groups.newCustoms.push({
        temporary_id: Date.now(),
        name: '',
        match_type_id: 1,
        field_group_id: undefined
      })
      nextTick(() => {
        (customNameInputs.value[(customNameInputs.value.length - 1)] as HTMLInputElement).focus()
      })
    }

    const addCustom = async (newCustom: CustomForm) => {
      try {
        const { name, match_type_id, field_group_id } = newCustom
        if (name === '' || match_type_id == undefined || field_group_id == undefined) return

        const { season_id } = route.params

        const dataObj = {
          name,
          match_type_id,
          field_group_id
        }

        const res = await SeasonGroupRepository.postParentGroup(season_id, dataObj)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          data._new.newGroups = []
          groups.list.push(data._new)

          const newCustomIndex = groups.newCustoms.findIndex(custom => custom.temporary_id === newCustom.temporary_id)
          groups.newCustoms.splice(newCustomIndex, 1)

          store.dispatch('addFlashMessage', {
            type: 'success',
            message: 'リーグを追加しました'
          })
        }
      } catch (error) {
        //
      }
    }

    const updateCustom = async (item: CustomForm) => {
      try {
        const { id, name, match_type_id, field_group_id } = item
        const { season_id } = route.params

        const dataObj = {
          custome_id: id,
          name,
          match_type_id,
          field_group_id
        }

        const res = await SeasonGroupRepository.updateParentGroup(season_id, dataObj)

        if (res.status === 200) {
          store.dispatch('addFlashMessage', {
            type: 'success',
            message: 'リーグの情報を変更しました'
          })
        }
      } catch (error) {
        //
      }
    }

    const updateCustomPriority = async () => {
      try {
        let priorities: {
          id: number
          priority: number
        }[] = []

        groups.list.forEach((custom, index) => {
          priorities.push({
            id: custom.id!,
            priority: index + 1
          })
        })

        const { season_id } = route.params

        const dataObj = {
          priorities: priorities
        }

        const res = await SeasonGroupRepository.updateParentGroupsPriority(season_id, dataObj)

        if (res.status === 200) {
          store.dispatch('addFlashMessage', {
            type: 'success',
            message: 'リーグの並び順を変更しました'
          })
        }
      } catch (error) {
        //
      }
    }

    const deleteNewCustom = (newCustom: CustomForm) => {
      const newCustomIndex = groups.newCustoms.findIndex(custom => custom.temporary_id === newCustom.temporary_id)
      groups.newCustoms.splice(newCustomIndex, 1)
    }

    const customDialogDelete = reactive<Dialog>({
      title: ' を削除しますか？',
      message: 'リーグを削除すると、下に作られているブロックも閲覧・変更することができなくなります。',
      confirmText: '削除する',
      cancelText: 'キャンセル',

      isOpen: false
    })

    const openCustomDialogDelete = (custom: CustomForm) => {
      customDialogDelete.title = custom.name + ' を削除しますか？'
      groups.selectedCustom = custom

      customDialogDelete.isOpen = true
    }

    const closeCustomDialogDelete = () => {
      customDialogDelete.isOpen = false
      groups.selectedCustom = undefined
    }

    const deleteCustom = async () => {
      try {
        const { season_id } = route.params

        const dataObj = {
          custome_id: groups.selectedCustom?.id
        }

        const res = await SeasonGroupRepository.deleteParentGroup(season_id, dataObj)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          getGroups()
          closeCustomDialogDelete()

          store.dispatch('addFlashMessage', {
            type: 'success',
            message: 'リーグを削除しました'
          })
        }
      } catch (error) {
        //
      }
    }

    const addGroupRow = (item: CustomForm) => {
      const newGroupList = item.newGroups
      if (newGroupList == undefined) return

      newGroupList.push({
        temporary_id: Date.now(),
        group_code: '',
        name: ''
      })
    }

    const addGroup = async (newGroup: GroupForm, parentCustom: CustomForm) => {
      try {
        const { group_code, name } = newGroup
        if (group_code === '' || name === '') return

        const { season_id } = route.params

        const dataObj = {
          custome_id: parentCustom.id,
          group_code,
          name
        }

        const res = await SeasonGroupRepository.postChildGroup(season_id, dataObj)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          parentCustom.groups!.push(data._new)

          const newGroupIndex = parentCustom.newGroups!.findIndex(group => group.temporary_id === newGroup.temporary_id)
          parentCustom.newGroups?.splice(newGroupIndex, 1)

          store.dispatch('addFlashMessage', {
            type: 'success',
            message: 'ブロックを追加しました'
          })
        }
      } catch (error) {
        //
      }
    }

    const updateGroup = async (group: GroupForm) => {
      try {
        const { id, group_code, name } = group
        const { season_id } = route.params

        const dataObj = {
          group_id: id,
          group_code,
          name
        }

        const res = await SeasonGroupRepository.updateChildGroup(season_id, dataObj)

        if (res.status === 200) {
          store.dispatch('addFlashMessage', {
            type: 'success',
            message: 'ブロックの情報を変更しました'
          })
        }
      } catch (error) {
        //
      }
    }

    const updateGroupPriority = async (groups: GroupForm[]) => {
      try {
        let priorities: {
          id: number
          priority: number
        }[] = []

        if (groups == undefined) return

        groups.forEach((group, index) => {
          priorities.push({
            id: group.id as number,
            priority: index + 1
          })
        })

        const { season_id } = route.params

        const dataObj = {
          priorities: priorities
        }

        const res = await SeasonGroupRepository.updateChildGroupsPriority(season_id, dataObj)

        if (res.status === 200) {
          store.dispatch('addFlashMessage', {
            type: 'success',
            message: 'ブロックの並び順を変更しました'
          })
        }
      } catch (error) {
        //
      }
    }

    const deleteNewGroup = (Newgroup: GroupForm, parentCustom: CustomForm) => {
      const currentNewGroups = parentCustom.newGroups!
      const index = currentNewGroups.findIndex(group => group.temporary_id === Newgroup.temporary_id)
      currentNewGroups.splice(index, 1)
    }

    const groupDialogDelete = reactive<Dialog>({
      title: ' を削除しますか？',
      message: '削除テキスト',
      confirmText: '削除する',
      cancelText: 'キャンセル',

      isOpen: false
    })

    const openGroupDialogDelete = (group: GroupForm, parentCustomIndex: number) => {
      const parentCustom = groups.list[parentCustomIndex]
      groups.selectedGroup = group
      groups.selectedCustom = parentCustom

      groupDialogDelete.title = `${parentCustom.name} ${group.name} を削除しますか？`
      groupDialogDelete.isOpen = true
    }

    const closeGroupDialogDelete = () => {
      groupDialogDelete.isOpen = false
      groups.selectedGroup = undefined
    }

    const deleteGroup = async () => {
      try {
        const { season_id } = route.params

        const selectedGroup = groups.selectedGroup
        if (selectedGroup == undefined) return

        const dataObj = {
          group_id: selectedGroup.id
        }

        const res = await SeasonGroupRepository.deleteChildGroup(season_id, dataObj)

        if (res.status === 200) {
          closeGroupDialogDelete()

          const selectedCustom = groups.selectedCustom
          const groupIndex = selectedCustom!.groups!.findIndex(group => group.id == selectedGroup.id)
          selectedCustom!.groups!.splice(groupIndex, 1)

          store.dispatch('addFlashMessage', {
            type: 'success',
            message: 'ブロックを削除しました'
          })
        }
      } catch (error) {
        //
      }
    }

    useHead({
      title: computed(() => 'グループ設定 - ' + season.data?.topic_name + ' ' + season.data?.name)
    })

    const breadcrumbs = computed(() => {
      return [
        {
          type: 'link',
          link: {
            name: 'イベント一覧',
            path: '/event'
          }
        },
        {
          type: 'link',
          link: {
            name: '大会一覧',
            path: '/'
          }
        },
        {
          type: 'link',
          size: 'large',
          link: {
            name: season.data?.topic_name,
            secondName: season.data?.name,
            path: '/event/' + route.params.topic_id + '/season/' + route.params.season_id,
          },
          setting: {
            path: '/event/' + route.params.topic_id
          }
        },
        {
          type: 'text',
          text: {
            name: 'グループ設定'
          }
        },
      ] as Breadcrumb[]
    })

    return {
      season,

      groups,
      getGroups,

      addCustomBlock,
      customNameInputs,
      addCustom,
      updateCustom,
      updateCustomPriority,

      deleteNewCustom,
      customDialogDelete,
      openCustomDialogDelete,
      closeCustomDialogDelete,
      deleteCustom,

      addGroupRow,
      addGroup,
      updateGroup,
      updateGroupPriority,

      deleteNewGroup,
      groupDialogDelete,
      openGroupDialogDelete,
      closeGroupDialogDelete,
      deleteGroup,

      fieldGroups,
      getFieldGroups,

      breadcrumbs
    }
  }
})
